import { useEffect, type FunctionComponent } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Route, Routes } from 'react-router-dom'

import { datadogLogs } from '@datadog/browser-logs'
import { Layout } from '@matillion/component-library'
import { HubHeader, RoleGuard, useAuth, useUser } from '@matillion/hub-client'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import {
  AGENT_ID,
  BRANCH_ID,
  ENVIRONMENT_ID,
  PROJECT_ID
} from '__test-utils__/ids'

import { useFlags } from 'hooks/useFlags'

import { Canvas } from 'modules/Canvas'
import { Login, Logout } from 'modules/core/Auth'
import CanvasErrorBoundaryFallback from 'modules/core/CanvasErrorBoundaryFallback'
import ErrorBoundaryFallback from 'modules/core/ErrorBoundaryFallback'
import EtlDesigner from 'modules/core/EtlDesigner'
import { ProjectGuard } from 'modules/core/ProjectGuard'
import FlowStatsExplorer from 'modules/FlowStatsExplorer/FlowStatsExplorer'
import { GettingStarted } from 'modules/GettingStarted'
import PipelineRunExplorer from 'modules/observability/PipelineRunExplorer/PipelineRunExplorer'

import { identify } from 'utils/heap'

/* istanbul ignore next */
const DevRoutes = () => (
  <ul data-testid="test-routes" style={{ margin: '24px' }}>
    <li>
      <a
        href={`/project/${PROJECT_ID}/branch/${BRANCH_ID}?agentId=${AGENT_ID}&environmentId=${ENVIRONMENT_ID}`}
      >
        DEV THING
      </a>
    </li>
    <li>
      <a href="/empty/empty">EMPTY</a>
    </li>
    <li>
      <a href="/500/500">Error response</a>
    </li>
  </ul>
)

const App: FunctionComponent = () => {
  const { isLoggedIn } = useAuth()
  const { user } = useUser()
  const { enableEosTaskHistory } = useFlags()

  useEffect(() => {
    identify(user.email)
  }, [user])

  if (!isLoggedIn) {
    return <Login />
  }

  return (
    <RoleGuard role="saas-etl:user">
      <Layout header={<HubHeader />}>
        <ErrorBoundary
          onError={(err) => {
            datadogLogs.logger.error(
              `Error caught by react error boundary (${err.message})`,
              {
                message: err.message,
                stack: err.stack,
                name: err.name
              }
            )
          }}
          fallback={<ErrorBoundaryFallback />}
        >
          <Routes>
            <Route path="/logout" element={<Logout />} />

            {process.env.REACT_APP_USE_MOCKS === 'true' &&
              !('Cypress' in window) && (
                <Route path="/" element={<DevRoutes />} />
              )}

            <Route path="project/:projectId" element={<ProjectGuard />}>
              <Route path="branch/:branchId" element={<EtlDesigner />}>
                <Route index element={<GettingStarted />} />

                <Route
                  path="task/:taskId"
                  element={
                    enableEosTaskHistory ? (
                      <PipelineRunExplorer />
                    ) : (
                      <FlowStatsExplorer />
                    )
                  }
                />

                <Route
                  path="job/:jobSummaryId"
                  element={
                    <ErrorBoundary
                      onError={(err) => {
                        /* istanbul ignore next */
                        datadogLogs.logger.error(
                          `Error caught by react canvas error boundary (${err.message})`,
                          {
                            message: err.message,
                            stack: err.stack,
                            name: err.name
                          }
                        )
                      }}
                      fallback={<CanvasErrorBoundaryFallback />}
                    >
                      <Canvas />
                    </ErrorBoundary>
                  }
                >
                  <Route path="component/:componentId" element={null} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<ProjectGuard />} />
          </Routes>

          {process.env.REACT_APP_REACT_QUERY_DEVTOOL === 'true' && (
            <section aria-label="React Query Devtools">
              <ReactQueryDevtools />
            </section>
          )}
        </ErrorBoundary>
      </Layout>
    </RoleGuard>
  )
}

export default App
